import { ChangeDetectorRef, Directive, HostBinding, inject, Input } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import { MathExpressionService } from './math-expression/math-expression.service';
import type { MathOperationRef } from './math-expression/math-operation-ref';

@Directive()
export abstract class MathOperation {
  @Input() fontFamily: string = 'Nunito';

  @Input() fontWeight: number = 800;

  @Input() color: string = '#000';

  @HostBinding('style.flex')
  @Input() flex: number | null = null;

  fontSize: number = 56;

  protected readonly _cdr = inject(ChangeDetectorRef);

  private _height: number = 0;

  public get height(): number {
    return this._height;
  }

  private _width: number = 0;

  public get width(): number {
    return this._width;
  }

  private _viewBox: string = '0 0 100 60';

  get viewBox(): string {
    return this._viewBox;
  }

  protected readonly _mathExpressionService?: MathExpressionService;

  private readonly _ref?: MathOperationRef;

  constructor() {
    this._mathExpressionService = inject(MathExpressionService, {
      optional: true,
    });
    if (this._mathExpressionService) {
      this._ref = this._mathExpressionService.declareMathOperation(this);
      this._mathExpressionService.restrictions.subscribe();
      this._mathExpressionService.changes.subscribe((res) => {
        if (res.fontFamily) this.fontFamily = res.fontFamily;
        if (res.fontWeight) this.fontWeight = res.fontWeight;
        if (res.color) this.color = res.color;
        if (res.flex !== undefined) this.flex = res.flex;
      });
    }
  }

  protected _setViewBox(width: number, height: number) {
    this._viewBox = `0 0 ${width} ${height}`;
    this._ref?.viewBoxUpdated(width, height);
    this._mathExpressionService.reserve(this, width / height);
    this._cdr.markForCheck();
  }

  setAvailableSpace({ width, height }: { width: number; height: number }) {
    this._height = height;
    this._width = width;
  }

  ngOnDestroy() {
    this._mathExpressionService.remove(this);
    this._ref?.destroy();
  }
}

@Directive()
export abstract class MathOperationWritable extends MathOperation implements ControlValueAccessor {
  @Input() readonly: boolean = false;

  protected onChange(value: any) {
    this._onChange(value);
    this._mathExpressionService.valueChanges.next(void 0);
  }

  private _onChange: any = () => {};

  protected _onTouched: any = () => {};

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  abstract writeValue(obj: any): void;

  abstract setDisabledState?(isDisabled: boolean): void;
}
